import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { DeliveriesForPlace } from 'types/model/order'
import { DeliveryShipment } from 'types/model/delivery'
import { useOrdersForPlace } from 'hooks/useRoutePlanning'
import { format } from 'date-fns'
import { Delivery, MarkerInfo } from 'types/api/delivery'
import { Order } from 'types/api/order'
import theme from 'lib/theme'

type OrdersQueryProps = {
  placeId: string
  startDate: string
  endDate: Date
  children: ReactNode
}

export type ToggleDeliveriesToDisplay = 'all' | 'unrouted' | number

interface RoutePlanningContextTypes {
  deliveries?: DeliveryShipment[]
  setToggleDeliveriesToDisplay: Dispatch<
    SetStateAction<ToggleDeliveriesToDisplay>
  >
  toggleDeliveriesToDisplay: ToggleDeliveriesToDisplay
  ordersToDisplay: DeliveriesForPlace[]
  shipmentsInOrder: DeliveriesForPlace[]
  deliveriesFromApi?: Delivery[]
  ordersFromApi?: Order[]
  setShipmentsInOrder: Dispatch<SetStateAction<DeliveriesForPlace[]>>
  filterUnroutedOrdersBasedOnGivenTime: (startTime: Date, endTime: Date) => void
  setOrdersToDisplay: Dispatch<SetStateAction<DeliveriesForPlace[]>>
  orderData: DeliveriesForPlace[] | undefined
  selectedOrders: {
    orderId: string
    deliveryId: string | number
    latlng: string
    routeName: string
  }[]
  setSelectedOrders: Dispatch<
    SetStateAction<
      {
        orderId: string
        deliveryId: string | number
        latlng: string
        routeName: string
      }[]
    >
  >
  getMarkers: () => { [key: string]: MarkerInfo[] }
  markerHovered: { id: string; latlng: string } | null
  setMarkerHovered: Dispatch<
    SetStateAction<{ id: string; latlng: string } | null>
  >
}

const RoutePlanningContext = createContext<RoutePlanningContextTypes | null>(
  null
)

const getOrderIndex = (order: DeliveriesForPlace) => {
  return order.shipmentData?.length > 0
    ? order.shipmentData[0].orderIds.indexOf(String(order.parts[0].id)) || 0
    : 0
}

// routed orders go before unrouted orders
const sortOrders = (orders: DeliveriesForPlace[]) => {
  orders?.sort((order1, order2) => {
    if (order2.routeIsInPlanning && !order1.routeIsInPlanning) return 1
    if (order1.routeIsInPlanning && !order2.routeIsInPlanning) return -1
    if (!order1.routeIsInPlanning && !order2.routeIsInPlanning) {
      return Number(order1.routeName) - Number(order2.routeName)
    }
    if (order1.routeName > order2.routeName) return 1
    if (order1.routeName < order2.routeName) return -1
    if (order1.routeName === order2.routeName) {
      const orderId1 = getOrderIndex(order1)
      const orderId2 = getOrderIndex(order2)
      return orderId1 - orderId2
    }
    return 0
  })
}

const RoutePlanningProvider = ({
  placeId,
  startDate,
  endDate,
  children,
}: OrdersQueryProps) => {
  const {
    ordersLoading,
    deliveriesLoading,
    hasNextPage,
    deliveries,
    combinedData: orderData,
    deliveriesFromApi,
    orders,
  } = useOrdersForPlace(placeId, startDate, endDate)
  const [ordersToDisplay, setOrdersToDisplay] = useState<DeliveriesForPlace[]>(
    []
  )
  const [markerHovered, setMarkerHovered] = useState<{
    id: string
    latlng: string
  } | null>(null)
  const [temp, setTemp] = useState<DeliveriesForPlace[]>([])
  const [toggleDeliveriesToDisplay, setToggleDeliveriesToDisplay] =
    useState<ToggleDeliveriesToDisplay>('all')
  const [shipmentsInOrder, setShipmentsInOrder] = useState<
    DeliveriesForPlace[]
  >([])
  const [selectedOrders, setSelectedOrders] = useState<
    {
      orderId: string
      deliveryId: string | number
      latlng: string
      routeName: string
    }[]
  >([])

  useEffect(() => {
    if (!ordersLoading && !deliveriesLoading && !hasNextPage) {
      sortOrders(orderData as DeliveriesForPlace[])
      setTemp(orderData as DeliveriesForPlace[])
      setOrdersToDisplay(orderData as DeliveriesForPlace[])
    }
  }, [ordersLoading, deliveriesLoading, startDate, hasNextPage])

  useEffect(() => {
    if (temp && temp.length > 0 && !ordersLoading && !deliveriesLoading) {
      if (toggleDeliveriesToDisplay === 'all') {
        sortOrders(temp)
        setOrdersToDisplay(temp)
      } else if (toggleDeliveriesToDisplay === 'unrouted') {
        const unroutedOrders = temp.filter(
          (orders) => !orders.routeIsInPlanning
        )
        setOrdersToDisplay(unroutedOrders)
      } else {
        const order = temp.filter((orders) =>
          Number(
            orders.shipmentData.some(
              (data) => Number(data.id) === Number(toggleDeliveriesToDisplay)
            )
          )
        )
        if (order && order.length > 0) {
          const orderIds =
            (order?.[0].shipmentData &&
              order?.[0].shipmentData.length > 0 &&
              order?.[0].shipmentData?.[0].orderIds) ||
            []
          order?.sort((a, b) => {
            const id1 = a.parts[0].id
            const id2 = b.parts[0].id
            return orderIds.indexOf(id1) - orderIds.indexOf(id2)
          })
          setOrdersToDisplay(order)
          setShipmentsInOrder(order as DeliveriesForPlace[])
        }
      }
    }
  }, [toggleDeliveriesToDisplay, temp])

  const filterUnroutedOrdersBasedOnGivenTime = useCallback(
    (startTime: Date, endTime: Date) => {
      const startHour = parseFloat(format(startTime, 'HH.mm'))
      const endHour = parseFloat(format(endTime, 'HH.mm'))
      if (ordersToDisplay && ordersToDisplay.length > 0) {
        const data = ordersToDisplay.filter((order) => {
          const orderStartTime = parseFloat(
            order.orderDeliveryTimes.startTime.replace(':', '.')
          )
          const orderEndTime = parseFloat(
            order.orderDeliveryTimes.endTime.replace(':', '.')
          )
          return startHour <= orderStartTime || endHour >= orderEndTime
        })

        setOrdersToDisplay(data)
      }
    },
    []
  )

  const getMarkers = useCallback(
    () =>
      ordersToDisplay.reduce((acc, order) => {
        const { latitude, longitude } =
          order.parts[0].deliveryMethod.service.address
        const latlng = `${latitude},${longitude}`

        const routeNameIndex =
          toggleDeliveriesToDisplay === 'all'
            ? ordersToDisplay
                .filter(
                  (orderToDisplay) =>
                    orderToDisplay.routeName === order.routeName
                )
                .findIndex(
                  (element) => element.parts[0].id === order.parts[0].id
                ) + 1
            : shipmentsInOrder.findIndex(
                (element) => element.parts[0].id === order.parts[0].id
              ) + 1
        const backgroundColor = selectedOrders.find(
          (selectedOrder) => selectedOrder.orderId === order.parts[0].id
        )
          ? theme.palette.primary.main
          : theme.palette.commonColors.white

        const {
          parts: [
            {
              deliveryMethod: {
                service: {
                  deliverySlot: { startTime, endTime },
                },
              },
            },
          ],
        } = order

        const time = `${startTime} - ${endTime}`

        const routeInfo = {
          routeNameIndex,
          backgroundColor,
          color: order.color,
          routeName: order.routeName,
          time,
          routeIsInPlanning: order.routeIsInPlanning,
          status: order.status,
          orderId: order.parts[0].id,
          deliveryId: order.parts[0].deliveryId,
          shipmentData: order.shipmentData,
        }

        if (acc[latlng]) {
          acc[latlng].push(routeInfo)
        } else {
          acc[latlng] = [routeInfo]
        }

        return acc
      }, {} as { [key: string]: MarkerInfo[] }),
    [ordersToDisplay]
  )

  return (
    <RoutePlanningContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        deliveries,
        ordersToDisplay,
        setOrdersToDisplay,
        setToggleDeliveriesToDisplay,
        toggleDeliveriesToDisplay,
        shipmentsInOrder,
        deliveriesFromApi,
        ordersFromApi: orders,
        setShipmentsInOrder,
        filterUnroutedOrdersBasedOnGivenTime,
        orderData,
        selectedOrders,
        setSelectedOrders,
        getMarkers,
        markerHovered,
        setMarkerHovered,
      }}>
      {children}
    </RoutePlanningContext.Provider>
  )
}

export const useRoutePlanning = () =>
  useContext(RoutePlanningContext) as RoutePlanningContextTypes

export default RoutePlanningProvider
